
















































import { Component, Vue as VueDecorator } from "vue-property-decorator";
import { COLOR_BUTTON_GREEN, COLOR_BUTTON } from "@/utils/colors";
import { themedFill, themedFillTextClass } from "@/utils/Theme";
import { getClientId } from "@/data/repository/ClientRepository";
import { ClientsInfoOfGroup } from "@/data/models/Client";
import { LoginResult } from "./LoginTypes";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { signInRefusalMessageKey, toSignInRefusalReason } from "@/utils/SignInRefusal";
import { setActiveUser, signInWithSharedUserOfGroup } from "@/data/repository/UserRepository";
import { auth } from "firebase";
import SearchFieldComponent from "@/components/common/SearchField.vue";

/** Show the search field only once the list is long enough to warrant it. */
const SEARCH_THRESHOLD = 8;

@Component({
  components: {
    "search-field": SearchFieldComponent,
  },
})
export default class SharedUserOfGroupOptionsDialogComponent extends VueDecorator {
  isShowing = false;
  isLoading = false;

  resolve?: (value: LoginResult) => void = undefined;
  reject?: (reason: string) => void = undefined;

  clientsInfoOfGroup: ClientsInfoOfGroup[] = [];
  calledViaHeader = false;

  searchQuery = "";

  get showSearch() {
    return this.clientsInfoOfGroup.length > SEARCH_THRESHOLD;
  }

  get filteredClientsInfoOfGroup() {
    const query = this.searchQuery.toLowerCase().trim();

    if (query === "") {
      return this.clientsInfoOfGroup;
    }

    return this.clientsInfoOfGroup.filter(
      (clientInfo) =>
        (this.getName(clientInfo) ?? "").toLowerCase().indexOf(query) >= 0
    );
  }

  onSearchQueryChanged(searchQuery: string) {
    this.searchQuery = searchQuery;
  }

  userId = "";
  emailAddress = "";
  memberClientIds: string[] = [];

  /** The option's fill for the active theme: the palette green and the accent red in dark mode. */
  getOptionFill(clientInfo: ClientsInfoOfGroup) {
    const color = this.getOptionColor(clientInfo);

    return color ? themedFill(color) : color;
  }

  /** The option's label class: white in light mode; dark on the palette green in dark mode. */
  getOptionTextClass(clientInfo: ClientsInfoOfGroup) {
    const color = this.getOptionColor(clientInfo);

    return color ? themedFillTextClass(color, "white-font") : "white-font";
  }

  getOptionColor(clientInfo: ClientsInfoOfGroup) {
    const activeClientId = getClientId();

    if (!activeClientId) {
      return false;
    }
    
    return activeClientId === clientInfo.clientId ? COLOR_BUTTON_GREEN : COLOR_BUTTON;
  }

  getName(clientInfo: ClientsInfoOfGroup) {
    return clientInfo.shortName || clientInfo.companyName || clientInfo.clientName || null;
  }

  async onClientSelected(
    clientInfo: ClientsInfoOfGroup
  ) {
    const activeClientId = getClientId();

    if (
      !activeClientId ||
      activeClientId === clientInfo.clientId
    ) {
      return;
    }

    this.isLoading = true;

    const result = await signInWithSharedUserOfGroup(
      this.emailAddress,
      clientInfo.clientId,
    );

    if (result.wasSuccessful && result.token) {
      await auth().signInWithCustomToken(result.token);

      const setUserResult = await setActiveUser(
        this.userId
      );

      this.isLoading = false;

      if (setUserResult != null) {
        this.isShowing = false;
        this.resolve && this.resolve(setUserResult);
      }
    }

    // #2610066: the client or user switched to is blocked; say why instead of doing nothing.
    const refusal = toSignInRefusalReason(result.reason);
    if (refusal) {
      SnackbarModule.showError({ key: signInRefusalMessageKey(refusal) });
    }

    // TODO: Add error
    this.isLoading = false;
  }

  async onBack() {
    this.isShowing = false;
  }

  async show(
    userId: string,
    emailAddress: string,
    clientsInfoOfGroup: Map<string, ClientsInfoOfGroup>,
    calledViaHeader: boolean | null = null,
  ) {
    this.userId = userId;
    this.emailAddress = emailAddress;
    this.memberClientIds = Array.from(clientsInfoOfGroup.keys());

    if (calledViaHeader) {
      this.calledViaHeader = true;
    }

    this.clientsInfoOfGroup = Array.from(clientsInfoOfGroup.values())
      .sort((a: ClientsInfoOfGroup, b: ClientsInfoOfGroup) => {
        const nameA = (a.shortName || a.companyName || a.clientName) ?? "";
        const nameB = (b.shortName || b.companyName || b.clientName) ?? "";
        
        const isASelected = this.memberClientIds.includes(a.clientId);
        const isBSelected = this.memberClientIds.includes(b.clientId);

        if (isASelected && !isBSelected) return -1;
        if (!isASelected && isBSelected) return 1;

        return nameA.localeCompare(nameB);
      });

    this.searchQuery = "";
    this.isShowing = true;

    return new Promise<LoginResult>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
}
